import {
  LibraryNameInput,
  LibraryNameInputRef,
} from '@components/LibraryNameInput/LibraryNameInput';
import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { ActionButton } from '@pages/ActionButton';
import { Library } from '@stores/Library/Library';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useModalRegistration } from '@stores/ModalStore';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { DEFAULT_LIBRARY_NAME, submitCreateLibrary } from '../createLibraryFlow';
import { CustomWordHintsToggle } from '../CustomWordHintsToggle';
import { SkipVisualIndexingToggle } from '../SkipVisualIndexingToggle';
import css from './MobileCreateLibraryDrawer.module.scss';

type MobileCreateLibraryDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  /** Prefill the name field when opened. */
  defaultName?: string;
  /**
   * When provided, hand the created library back to the caller instead of
   * navigating to it (skips the toast) — see LibraryCreateModalOpenOptions.
   */
  onCreated?: (library: Library) => void;
};

export const MobileCreateLibraryDrawer = observer(
  ({ open, onClose, onOpen, defaultName, onCreated }: MobileCreateLibraryDrawerProps) => {
    const [newName, setNewName] = useState('');
    const [skipVisuals, setSkipVisuals] = useState(false);
    // "Use custom word hints" → per-library transcription glossary (persisted on
    // create as settings.v1.glossary).
    const [useWordHints, setUseWordHints] = useState(false);
    const [wordHints, setWordHints] = useState<string[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const inputRef = useRef<LibraryNameInputRef>(null);
    const libraryManager = useLibraryManager();
    const navigate = useNavigate();

    // Shared id — LibraryCreateModal renders this drawer on mobile and registers too, so the
    // count reaches 2 and refcounting is what keeps it balanced.
    useModalRegistration('library-create', open);

    // Seed the name field from `defaultName` and reset the switch each time it opens.
    useEffect(() => {
      if (open) {
        setNewName(defaultName ?? '');
        setSkipVisuals(false);
        setUseWordHints(false);
        setWordHints([]);
      }
    }, [open, defaultName]);

    const handleSettled = () => {
      inputRef.current?.focus();
    };

    const handleClose = () => {
      setNewName('');
      setSkipVisuals(false);
      setUseWordHints(false);
      setWordHints([]);
      onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsCreating(true);
      try {
        await submitCreateLibrary({
          libraryManager,
          navigate,
          name: newName,
          skipVisuals,
          wordHints: useWordHints ? wordHints : [],
          onCreated,
        });
        setNewName('');
        handleClose();
      } catch (error) {
        console.error(error);
      } finally {
        setIsCreating(false);
      }
    };

    return (
      <MobileDrawer
        open={open}
        onClose={handleClose}
        onOpen={onOpen}
        frosted
        onSettled={handleSettled}
      >
        <div className={css.content}>
          <span className={css.title}>Create a new library</span>
          <form onSubmit={handleSubmit} className={css.form}>
            <div className={css.fieldGroup}>
              <label htmlFor="mobile-library-name" className={css.label}>
                Library name
              </label>
              <LibraryNameInput
                ref={inputRef}
                id="mobile-library-name"
                placeholder={DEFAULT_LIBRARY_NAME}
                value={newName}
                onValueChange={setNewName}
              />
            </div>
            <SkipVisualIndexingToggle checked={skipVisuals} onChange={setSkipVisuals} />
            <CustomWordHintsToggle
              checked={useWordHints}
              onChange={setUseWordHints}
              words={wordHints}
              onWordsChange={setWordHints}
            />
            <ActionButton type="submit" fullWidth disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create library'}
            </ActionButton>
          </form>
        </div>
      </MobileDrawer>
    );
  },
);
